import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { aboutService } from '../../services/aboutService';
import { uploadImage } from '../../services/imageUpload';
import { Edit, Save, PlusCircle, Trash2, X, UploadCloud, } from 'lucide-react';

const AboutContentManager = ({ setEditingAbout, updatePageLastUpdated }) => {
  const [aboutContent, setAboutContent] = useState(null);
  const [loadingAboutContent, setLoadingAboutContent] = useState(false);
  const [selectedAboutSection, setSelectedAboutSection] = useState('hero');
  const { showError } = useError();

  useEffect(() => {
    const fetchAboutPageContent = async () => {
      setLoadingAboutContent(true);
      try {
        const content = await aboutService.getAboutContent();
        setAboutContent(content);
      } catch (error) {
        showError('Failed to load About page content: ' + error.message, 'error');
        setAboutContent(null);
      } finally {
        setLoadingAboutContent(false);
      }
    };
    fetchAboutPageContent();
  }, [showError]);

  const handleSaveAboutContent = async (sectionName, content) => {
    try {
      await aboutService.updateAboutSection(sectionName, content);
      showError(`${sectionName} section updated successfully!`, 'success');
      const updatedContent = await aboutService.getAboutContent();
      setAboutContent(updatedContent);
      await updatePageLastUpdated('About');
    } catch (error) {
      showError(`Failed to update ${sectionName} section: ${error.message}`, 'error');
    }
  };

  const handleAboutContentChange = (section, field, value) => {
    setAboutContent(prevContent => ({
      ...prevContent,
      [section]: {
        ...prevContent[section],
        [field]: value
      }
    }));
  };

  const handleAddArrayContent = (section, defaultItem) => {
    setAboutContent(prevContent => ({
      ...prevContent,
      [section]: [...(prevContent[section] || []), defaultItem]
    }));
  };

  const handleRemoveArrayContent = (section, indexToRemove) => {
    setAboutContent(prevContent => ({
      ...prevContent,
      [section]: (prevContent[section] || []).filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleUpdateArrayItemContent = (section, index, field, value) => {
    setAboutContent(prevContent => {
      const updatedArray = [...(prevContent[section] || [])];
      updatedArray[index] = {
        ...updatedArray[index],
        [field]: value
      };
      return {
        ...prevContent,
        [section]: updatedArray
      };
    });
  };

  const handleImageUpload = async (event, section, field, index = null) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      showError('Uploading image...', 'info');
      const imageUrl = await uploadImage(file);
      if (section === 'hero' || section === 'mission_vision') {
        handleAboutContentChange(section, field, imageUrl);
      } else if (section === 'team') {
        handleUpdateArrayItemContent('team', index, field, imageUrl);
      }
      showError('Image uploaded successfully!', 'success');
    } catch (error) {
      showError('Image upload failed: ' + error.message, 'error');
    }
  };

  if (loadingAboutContent) {
    return (
      <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading About page editor...</p>
        </div>
      </div>
    );
  }

  if (!aboutContent) {
    return (
      <div className="text-center py-8 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg shadow-md">
        <p className="font-semibold">Error:</p>
        <p>Could not load About page content for editing. Please try again.</p>
      </div>
    );
  }

  const currentSectionContent = aboutContent[selectedAboutSection];

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 p-1 md:p-6 rounded-xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-700 animate-fade-in">
      <div className="flex justify-between items-center mb-6 border-b pb-4 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit About Page Content</h2>
        <button
          onClick={() => setEditingAbout(false)}
          className="flex items-center  md:px-4 md:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 text-sm font-medium shadow-sm"
        >
          <X className="w-4 h-4 mr-2" /> Close
        </button>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2" aria-label="Tabs">
          {['hero', 'mission_vision', 'principles', 'glance_stats', 'team', 'cta'].map((section) => (
            <button
              key={section}
              onClick={() => setSelectedAboutSection(section)}
              className={`${
                selectedAboutSection === section
                  ? 'border-accent dark:border-accent text-accent dark:text-accent bg-accentdark:bg-accent'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-500'
              } whitespace-nowrap py-2 px-4 border-b-2 font-medium text-sm capitalize rounded-t-lg transition-all duration-200`}
            >
              {section.replace('_', ' ')}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-8">
        {/* Hero Section */}
        {selectedAboutSection === 'hero' && (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Hero Section (About Page specific)</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="hero_heading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Heading</label>
                <input
                  type="text"
                  id="hero_heading"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={currentSectionContent?.heading || ''}
                  onChange={(e) => handleAboutContentChange('hero', 'heading', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="hero_subheading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subheading</label>
                <textarea
                  id="hero_subheading"
                  rows="3"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={currentSectionContent?.subheading || ''}
                  onChange={(e) => handleAboutContentChange('hero', 'subheading', e.target.value)}
                ></textarea>
              </div>
              <div>
                <label htmlFor="hero_image" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hero Image</label>
                {currentSectionContent?.image && (
                  <div className="mb-2">
                    <img src={currentSectionContent.image} alt="Hero Preview" className="h-40 w-auto object-cover rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-sm">{currentSectionContent.image}</p>
                  </div>
                )}
                <input
                  type="text"
                  id="hero_image"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200 mb-2"
                  value={currentSectionContent?.image || ''}
                  onChange={(e) => handleAboutContentChange('hero', 'image', e.target.value)}
                  placeholder="Enter image URL or upload below"
                />
                <label className="flex items-center px-4 py-2 bg-accentdark:bg-accent text-accent dark:text-accent rounded-md border border-accent dark:border-accent cursor-pointer hover:bg-accent/30 dark:hover:bg-primary-700 transition duration-200 text-sm font-medium">
                  <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                  <input type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'hero', 'image')} />
                </label>
              </div>
            </div>
            <button
              onClick={() => handleSaveAboutContent('hero', aboutContent.hero)}
              className="mt-6 flex items-center p-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700/80 transition duration-200 font-semibold"
            >
              <Save className="w-4 h-4 mr-2" /> Save Hero Section
            </button>
          </div>
        )}

        {/* Mission & Vision Section */}
        {selectedAboutSection === 'mission_vision' && (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Mission & Vision Section</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="mission_heading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mission Heading</label>
                  <input
                    type="text"
                    id="mission_heading"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                    value={currentSectionContent?.mission_heading || ''}
                    onChange={(e) => handleAboutContentChange('mission_vision', 'mission_heading', e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="mission_text_1" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mission Text 1</label>
                  <textarea
                    id="mission_text_1"
                    rows="3"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                    value={currentSectionContent?.mission_text_1 || ''}
                    onChange={(e) => handleAboutContentChange('mission_vision', 'mission_text_1', e.target.value)}
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="mission_text_2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mission Text 2</label>
                  <textarea
                    id="mission_text_2"
                    rows="3"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                    value={currentSectionContent?.mission_text_2 || ''}
                    onChange={(e) => handleAboutContentChange('mission_vision', 'mission_text_2', e.target.value)}
                  ></textarea>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="vision_heading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vision Heading</label>
                  <input
                    type="text"
                    id="vision_heading"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                    value={currentSectionContent?.vision_heading || ''}
                    onChange={(e) => handleAboutContentChange('mission_vision', 'vision_heading', e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="vision_text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vision Text</label>
                  <textarea
                    id="vision_text"
                    rows="3"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                    value={currentSectionContent?.vision_text || ''}
                    onChange={(e) => handleAboutContentChange('mission_vision', 'vision_text', e.target.value)}
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="mission_vision_image" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image</label>
                  {currentSectionContent?.image && (
                    <div className="mb-2">
                      <img src={currentSectionContent.image} alt="Mission/Vision Preview" className="h-40 w-auto object-cover rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm" />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-sm">{currentSectionContent.image}</p>
                    </div>
                  )}
                  <input
                    type="text"
                    id="mission_vision_image"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200 mb-2"
                    value={currentSectionContent?.image || ''}
                    onChange={(e) => handleAboutContentChange('mission_vision', 'image', e.target.value)}
                    placeholder="Enter image URL or upload below"
                  />
                  <label className="flex items-center px-4 py-2 bg-accentdark:bg-accent text-accent dark:text-accent rounded-md border border-accent dark:border-accent cursor-pointer hover:bg-accent/30 dark:hover:bg-primary-700 transition duration-200 text-sm font-medium">

                    <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                    <input type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'mission_vision', 'image')} />
                  </label>
                </div>
              </div>
            </div>
            <button
              onClick={() => handleSaveAboutContent('mission_vision', aboutContent.mission_vision)}
              className="mt-6 flex items-center p-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold"
            >
              <Save className="w-4 h-4 mr-2" /> Save
            </button>
          </div>
        )}

        {/* Principles Section */}
        {selectedAboutSection === 'principles' && (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Principles List</h3>
            <div className="space-y-6">
              {currentSectionContent?.map((principle, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 relative">
                  <button onClick={() => handleRemoveArrayContent('principles', index)} className="absolute top-3 right-3 text-red-500 dark:text-red-300 hover:text-red-700 dark:hover:text-red-100 transition-colors duration-200 p-1 rounded-full bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700" title="Remove principle" >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                    <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200" value={principle.title} onChange={(e) => handleUpdateArrayItemContent('principles', index, 'title', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea rows="2" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200" value={principle.description} onChange={(e) => handleUpdateArrayItemContent('principles', index, 'description', e.target.value)} ></textarea>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex space-x-3">
              <button onClick={() => handleAddArrayContent('principles', { title: '', description: '', icon: '' })} className="flex items-center p-1 md:px-5 md:py-2.5 border border-accent dark:border-accent text-accent dark:text-accent rounded-lg hover:bg-primary-700dark:hover:bg-primary-700 transition duration-200 font-semibold text-sm" >
                <PlusCircle className="w-6 h-6 mr-2" /> Add New Principle
              </button>
              <button onClick={() => handleSaveAboutContent('principles', aboutContent.principles)} className="flex items-center px-5 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold" >
                <Save className=" w-4 h-4 mr-2" /> Save
              </button>
            </div>
          </div>
        )}

        {/* Glance Stats Section */}
        {selectedAboutSection === 'glance_stats' && (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Glance Stats List</h3>
            <div className="space-y-6">
              {currentSectionContent?.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 relative">
                  <button onClick={() => handleRemoveArrayContent('glance_stats', index)} className="absolute top-3 right-3 text-red-500 dark:text-red-300 hover:text-red-700 dark:hover:text-red-100 transition-colors duration-200 p-1 rounded-full bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700" title="Remove stat" >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number</label>
                    <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200" value={stat.number} onChange={(e) => handleUpdateArrayItemContent('glance_stats', index, 'number', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label</label>
                    <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200" value={stat.label} onChange={(e) => handleUpdateArrayItemContent('glance_stats', index, 'label', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex space-x-3">
              <button onClick={() => handleAddArrayContent('glance_stats', { number: '', label: '' })} className="flex items-center px-1 md:px-5 md:py-2.5 border border-accent dark:border-accent text-accent dark:text-accent rounded-lg hover:bg-primary-700dark:hover:bg-primary-700 transition duration-200 font-semibold text-sm" >
                <PlusCircle className="w-6 h-6 mr-2" /> Add New Stat
              </button>
              <button onClick={() => handleSaveAboutContent('glance_stats', aboutContent.glance_stats)} className="flex items-center px-5 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold" >
                <Save className="w-4 h-4 mr-2" /> Save
              </button>
            </div>
          </div>
        )}

        {/* Team Section */}
        {selectedAboutSection === 'team' && (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Team Members</h3>
            <div className="space-y-6">
              {currentSectionContent?.map((member, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 relative">
                  <button onClick={() => handleRemoveArrayContent('team', index)} className="absolute top-3 right-3 text-red-500 dark:text-red-300 hover:text-red-700 dark:hover:text-red-100 transition-colors duration-200 p-1 rounded-full bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700" title="Remove member" >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                      <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200" value={member.name} onChange={(e) => handleUpdateArrayItemContent('team', index, 'name', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                      <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200" value={member.role} onChange={(e) => handleUpdateArrayItemContent('team', index, 'role', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bio</label>
                      <textarea rows="3" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200" value={member.bio} onChange={(e) => handleUpdateArrayItemContent('team', index, 'bio', e.target.value)} ></textarea>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
                      {member.image && (
                        <div className="mb-2">
                          <img src={member.image} alt={`${member.name} Preview`} className="h-40 w-auto object-cover rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm" />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-sm">{member.image}</p>
                        </div>
                      )}
                      <input type="text" className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200 mb-2" value={member.image} onChange={(e) => handleUpdateArrayItemContent('team', index, 'image', e.target.value)} placeholder="Enter image URL or upload below" />
                      <label className="flex items-center px-4 py-2 bg-accentdark:bg-accent text-accent dark:text-accent rounded-md border border-accent dark:border-accent cursor-pointer hover:bg-accent/30 dark:hover:bg-primary-700 transition duration-200 text-sm font-medium">
                        <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                        <input type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'team', 'image', index)} />
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex space-x-3">
              <button onClick={() => handleAddArrayContent('team', { name: '', role: '', bio: '', image: '' })} className="flex items-center p-1 md:px-5 md:py-2.5 border border-accent dark:border-accent text-accent dark:text-accent rounded-lg hover:bg-primary-700dark:hover:bg-primary-700 transition duration-200 font-semibold text-sm" >
                <PlusCircle className="w-6 h-6 mr-2" /> Add New Member
              </button>
              <button onClick={() => handleSaveAboutContent('team', aboutContent.team)} className="flex items-center px-5 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold" >
                <Save className="w-4 h-4 mr-2" /> Save
              </button>
            </div>
          </div>
        )}

        {/* CTA Section */}
        {selectedAboutSection === 'cta' && (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Call to Action Section</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="cta_heading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Heading</label>
                <input
                  type="text"
                  id="cta_heading"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={currentSectionContent?.heading || ''}
                  onChange={(e) => handleAboutContentChange('cta', 'heading', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="cta_subheading" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subheading</label>
                <textarea
                  id="cta_subheading"
                  rows="3"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={currentSectionContent?.subheading || ''}
                  onChange={(e) => handleAboutContentChange('cta', 'subheading', e.target.value)}
                ></textarea>
              </div>
              <div>
                <label htmlFor="cta_button_text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Button Text</label>
                <input
                  type="text"
                  id="cta_button_text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={currentSectionContent?.button_text || ''}
                  onChange={(e) => handleAboutContentChange('cta', 'button_text', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="cta_button_link" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Button Link</label>
                <input
                  type="text"
                  id="cta_button_link"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={currentSectionContent?.button_link || ''}
                  onChange={(e) => handleAboutContentChange('cta', 'button_link', e.target.value)}
                />
              </div>
            </div>
            <button
              onClick={() => handleSaveAboutContent('cta', aboutContent.cta)}
              className="mt-6 flex items-center p-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold"
            >
              <Save className="w-4 h-4 mr-2" /> Save CTA Section
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutContentManager;